import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Check, Info, AlertTriangle, CheckCircle, BellRing, Ticket, Shield, FileText, ChevronRight } from "lucide-react";
import api from "../../lib/api";

const typeConfig = {
  info: { icon: Info, color: "#1B4F8A", bg: "#ddeaf9" },
  success: { icon: CheckCircle, color: "#0E8A55", bg: "#D4F2E7" },
  warning: { icon: AlertTriangle, color: "#D97706", bg: "#FEF6E4" },
  alert: { icon: AlertTriangle, color: "#C7322A", bg: "#FDE8E7" },
  support_ticket: { icon: Ticket, color: "#EA580C", bg: "#FFF7ED" },
  overdue_compliance: { icon: Shield, color: "#DC2626", bg: "#FEF2F2" },
  access_request: { icon: FileText, color: "#2563EB", bg: "#EFF6FF" },
};

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const NotificationBell = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [pushEnabled, setPushEnabled] = useState(false);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const [notifRes, actionRes] = await Promise.all([
        api.get("/notifications"),
        api.get("/action-center").catch(() => ({ data: { tasks: [], needs_attention: [] } })),
      ]);
      setNotifications(notifRes.data.notifications || []);
      setUnreadCount(notifRes.data.unread_count || 0);
      const items = [...(actionRes.data.tasks || []), ...(actionRes.data.needs_attention || [])];
      setActionItems(items.slice(0, 10));
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // WebSocket connection
  useEffect(() => {
    if (!token) return;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const wsUrl = backendUrl.replace("https://", "wss://").replace("http://", "ws://") + "/ws/notifications";
      const ws = new WebSocket(wsUrl);
      ws.onopen = () => { ws.send(JSON.stringify({ token })); };
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === "notification") {
          fetchNotifications();
        }
      };
      wsRef.current = ws;
    } catch (e) { /* WS not critical */ }
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [token]);

  // Check push subscription status
  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(reg => {
        reg.pushManager.getSubscription().then(sub => {
          setPushEnabled(!!sub);
        });
      });
    }
  }, []);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-read", []);
      fetchNotifications();
    } catch (e) { /* silent */ }
  };

  const enablePush = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error("Notification permission denied");
        return;
      }

      const vapidRes = await api.get("/push/vapid-key");
      const vapidKey = vapidRes.data.public_key;

      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      await api.post("/push/subscribe", subscription.toJSON());
      setPushEnabled(true);
      toast.success("Push notifications enabled");

      // Send test notification
      await api.post("/push/test", {});
    } catch (e) {
      console.error("Push subscription error:", e);
      toast.error("Failed to enable push notifications");
    }
  };

  if (!user) return null;

  const totalBadge = unreadCount + actionItems.length;

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      <button onClick={() => { setIsOpen(!isOpen); if (!isOpen) fetchNotifications(); }}
        className="relative p-2 rounded-lg hover:bg-muted transition-colors" data-testid="notification-bell-btn">
        <Bell className="w-5 h-5 text-muted-foreground" />
        {totalBadge > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {totalBadge > 9 ? "9+" : totalBadge}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-[400px] bg-background border rounded-xl shadow-xl z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="p-3 border-b">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-sm">Notifications & Alerts</h3>
              <div className="flex items-center gap-2">
                {!pushEnabled && 'PushManager' in window && (
                  <button onClick={enablePush} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid="enable-push-btn">
                    <BellRing className="w-3 h-3" /> Enable Push
                  </button>
                )}
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid="mark-all-read">
                    <Check className="w-3 h-3" /> Mark all read
                  </button>
                )}
              </div>
            </div>
            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 p-0.5 rounded-lg">
              {[
                { key: "all", label: "All", count: totalBadge },
                { key: "alerts", label: "Action Items", count: actionItems.length },
                { key: "notifications", label: "Notifications", count: unreadCount },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 text-xs py-1.5 px-2 rounded-md font-medium transition-colors ${activeTab === tab.key ? "bg-white text-[#0f1b2d] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid={`notif-tab-${tab.key}`}
                >
                  {tab.label} {tab.count > 0 && <span className="ml-1 text-[10px] bg-red-500 text-white rounded-full px-1.5">{tab.count}</span>}
                </button>
              ))}
            </div>
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {/* Action items */}
            {(activeTab === "all" || activeTab === "alerts") && actionItems.length > 0 && (
              <>
                {actionItems.map((item, idx) => {
                  const tc = typeConfig[item.type] || typeConfig.warning;
                  const Icon = tc.icon;
                  return (
                    <div
                      key={`action-${item.id || idx}`}
                      className="p-3 border-b hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => { if (item.action_url) { navigate(item.action_url); setIsOpen(false); } }}
                      data-testid={`action-item-${item.id}`}
                    >
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: tc.bg, color: tc.color }}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold">{item.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{item.description}</p>
                        </div>
                        {item.action_url && <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-2" />}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
            {/* Notifications */}
            {(activeTab === "all" || activeTab === "notifications") && (
              <>
                {notifications.length === 0 && actionItems.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground">
                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No notifications</p>
                  </div>
                ) : notifications.slice(0, 15).map(n => {
                  const tc = typeConfig[n.type] || typeConfig.info;
                  const Icon = tc.icon;
                  return (
                    <div key={n.id} className={`p-3 border-b hover:bg-muted/30 transition-colors cursor-default ${!n.is_read ? 'bg-blue-50/50' : ''}`} data-testid={`notif-item-${n.id}`}>
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: tc.bg, color: tc.color }}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!n.is_read ? 'font-semibold' : ''}`}>{n.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                          <p className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</p>
                        </div>
                        {!n.is_read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
          <div className="p-2 border-t bg-slate-50 text-center">
            <button onClick={() => { navigate("/action-center"); setIsOpen(false); }} className="text-xs text-[#1e3a5f] font-medium hover:underline" data-testid="view-all-link">
              View Action Center
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
